import { ROWS, COLS, colors, PIECE_SIZE } from '../core/constants.js';
import { executeGravityAnimation, executeRemovalAnimation } from '../shared/animations-public.js';

// Boardインスタンスはパラメータで渡される
import { enqueuePostSettle } from './activation-queue.js';
import { collectAllMatches, hasAvailableMove } from './board-evaluation.js';
import { createRemovalPlan } from './removal-plan.js';
import { updateScore } from './scoring.js';
import { initializeSpecialPieces } from './specials/manager.js';

function addPiece(board, row, col) {
  const color = colors[Math.floor(Math.random() * colors.length)];

  // ピースオブジェクト作成
  const piece = {
    color,
    position: [row, col],
  };

  // Board経由でピース追加
  board.setPiece(row, col, piece);
}

async function removePieces(board, matches, chain) {
  // カスケードライフサイクルとチェーンはcascade.jsで管理
  const currentChain = Number(chain) || 1;

  // ゲームロジック実行
  const gameLogic = () => {
    updateScore(matches, currentChain);

    const specialPieces = matches.filter((piece) => piece.specialType);
    const nonSpecialPieces = matches.filter((piece) => !piece.specialType);

    const removalPlan = createRemovalPlan(board, nonSpecialPieces, specialPieces, []);

    // Board更新を同期実行
    const waves = removalPlan?.waves || [];

    for (let waveIndex = 0; waveIndex < waves.length; waveIndex++) {
      const wave = waves[waveIndex];
      // transforms処理（削除より先に実行）
      const transforms = wave.transforms || [];
      for (let transformIndex = 0; transformIndex < transforms.length; transformIndex++) {
        const t = transforms[transformIndex];
        const transformedPiece = {
          ...t.to,
          position: [t.row, t.col],
        };
        board.setPiece(t.row, t.col, transformedPiece);

        // removalPlanのtransformsを完全なピースデータで更新
        // （IDを含む完全なデータをgraphics層に渡すため）
        t.piece = board.getPiece(t.row, t.col);

        // PSAQ（Post-Settle Activation Queue）にエンキュー
        // このピースはsettled後（落下・補充完了後）に発動する
        enqueuePostSettle(transformedPiece.id, {
          specialType: transformedPiece.specialType ?? null,
          chain: currentChain,
          waveIndex,
          transformIndex,
        });
      }

      // removes処理
      for (const r of wave.removes || []) {
        board.removePiece(r.row, r.col);
      }
    }

    return { removalPlan, matches, chain: currentChain };
  };

  // アニメーション実行
  const result = await gameLogic();
  await executeRemovalAnimation(result.removalPlan);

  // ★削除アニメーション完了後に特殊ピース生成
  try {
    await processPendingCreations(board, result.removalPlan);
  } catch (error) {
    // 生成処理失敗時もカスケードは継続（Board状態は更新済み）
    const { MinimalErrorTracker } = await import('../shared/log.js');
    MinimalErrorTracker.warn('GAME', 'Pending creation processing failed', error);
  }
}

/**
 * 削除完了後の特殊ピース生成処理
 * @param {Object} board - Boardインスタンス
 * @param {Object} removalPlan - 削除プラン
 */
async function processPendingCreations(board, removalPlan) {
  const { executeSpecialCreationAnimation } = await import('../shared/animations-public.js');
  const { MinimalErrorTracker } = await import('../shared/log.js');

  const waves = removalPlan?.waves || [];
  for (const wave of waves) {
    const pendingCreations = wave.pendingCreations || [];

    for (const creation of pendingCreations) {
      try {
        // Board更新（空セル → 特殊ピース）
        board.setPiece(creation.row, creation.col, creation.specialPiece);

        // 生成アニメーション実行
        await executeSpecialCreationAnimation(
          creation.row,
          creation.col,
          creation.specialPiece,
          creation.matchedColors,
        );
      } catch (error) {
        // 個別の生成失敗は記録するが、他の生成は継続
        MinimalErrorTracker.warn(
          'GAME',
          `Special piece creation failed at (${creation.row},${creation.col})`,
          error,
        );
      }
    }
  }
}

async function applyGravityAndFill(board) {
  // ゲームロジック実行
  const gameLogic = () => {
    const targetPieces = [];
    const initialYOffsets = [];
    const emptyCellCounts = new Array(COLS).fill(0);

    for (let col = 0; col < COLS; col++) {
      let emptyCells = 0;
      for (let row = ROWS - 1; row >= 0; row--) {
        const piece = board.getPiece(row, col);
        if (!piece) {
          emptyCells++;
        } else if (emptyCells > 0) {
          // Boardの移動APIを使用
          board.movePiece(row, col, row + emptyCells, col);
          targetPieces.push({ row: row + emptyCells, col });
          initialYOffsets.push(-emptyCells * PIECE_SIZE);
        }
      }
      emptyCellCounts[col] = emptyCells;
      let cumulativeDropDistance = 0;
      for (let i = 0; i < emptyCells; i++) {
        const row = i;
        addPiece(board, row, col);
        targetPieces.push({ row, col });
        initialYOffsets.push(-((row + emptyCellCounts[col]) * PIECE_SIZE) - cumulativeDropDistance);
        cumulativeDropDistance -= PIECE_SIZE;
      }
    }

    // アニメーション用にBoardの現在状態を取得
    const gridSnapshot = board.pieces.map((row) =>
      row.map((piece) => (piece ? { ...piece } : null)),
    );

    return { gridSnapshot, targetPieces, initialYOffsets };
  };

  // アニメーション実行
  const result = await gameLogic();
  await executeGravityAnimation(result.gridSnapshot, result.targetPieces, result.initialYOffsets);
}

/**
 * 指定セルの禁止色を計算
 * @private
 */
function getForbiddenColorsForCell(board, row, col) {
  const forbiddenColors = new Set();

  // 左2マス同色チェック
  if (col >= 2) {
    const left1 = board.getPiece(row, col - 1);
    const left2 = board.getPiece(row, col - 2);
    if (left1 && left2 && left1.color === left2.color) {
      forbiddenColors.add(left1.color);
    }
  }

  // 上2マス同色チェック
  if (row >= 2) {
    const up1 = board.getPiece(row - 1, col);
    const up2 = board.getPiece(row - 2, col);
    if (up1 && up2 && up1.color === up2.color) {
      forbiddenColors.add(up1.color);
    }
  }

  return forbiddenColors;
}

/**
 * 盤面を一度だけ生成試行
 * @private
 */
function tryGenerateBoard(board) {
  board.clear();

  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const forbiddenColors = getForbiddenColorsForCell(board, row, col);
      const candidates = colors.filter((c) => !forbiddenColors.has(c));

      if (candidates.length === 0) {
        return false; // 候補枯渇
      }

      const color = candidates[Math.floor(Math.random() * candidates.length)];
      const piece = { color, position: [row, col] };
      board.setPiece(row, col, piece);
    }
  }

  return true; // 生成成功
}

/**
 * マッチが発生しない盤面を生成
 * 左上から順にピースを配置し、直前2マスと同色にならないよう制約
 *
 * @param {Board} board - Boardインスタンス
 * @throws {Error} 最大リトライ回数を超えた場合
 */
async function seedBoardWithoutMatches(board) {
  const MAX_RETRIES = 10;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (!tryGenerateBoard(board)) continue;

    // 検証: マッチゼロ && 有効手あり
    const matches = collectAllMatches(board);
    if (matches.length === 0 && hasAvailableMove(board)) {
      return; // 成功
    }
  }

  // リトライ上限到達
  const { MinimalErrorTracker } = await import('../shared/log.js');
  MinimalErrorTracker.error('GAME', 'seedBoardWithoutMatches failed after max retries');
  throw new Error('Failed to generate no-match board');
}

/**
 * 盤面外から波状に落下する演出
 * 全ピースが画面外上部から降ってきて、列ごとに微妙な時間差で波状に見える
 * 毎回異なるパターンでランダム性を持たせる
 *
 * @param {Board} board - Boardインスタンス
 */
async function animateBoardWaveEntry(board) {
  const targetPieces = [];
  const initialYOffsets = [];

  // 全ピースを画面外上部に配置（最下行でも完全に見えない位置）
  const ABOVE_SCREEN = -(ROWS + 2) * PIECE_SIZE;

  // 波パターンをランダム選択
  const patterns = [
    (col) => col * PIECE_SIZE * 0.3, // 線形（左→右）
    (col) => Math.sin((col / COLS) * Math.PI * 0.5) * PIECE_SIZE * 1.2, // サイン波
    (col) => Math.abs(col - COLS / 2) * PIECE_SIZE * 0.3, // 両端→中央
    (col) => (COLS / 2 - Math.abs(col - COLS / 2)) * PIECE_SIZE * 0.3, // 中央→両端
  ];
  const selectedPattern = patterns[Math.floor(Math.random() * patterns.length)];

  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      if (board.getPiece(row, col)) {
        targetPieces.push({ row, col });

        // 基本パターン + ランダムジッター（最大±0.25ピース高さ）
        const baseDelay = selectedPattern(col);
        const jitter = (Math.random() - 0.5) * PIECE_SIZE * 0.3; // ±0.25
        const waveDelay = baseDelay + jitter;

        initialYOffsets.push(ABOVE_SCREEN + waveDelay);
      }
    }
  }

  const gridSnapshot = board.pieces.map((row) => row.map((piece) => (piece ? { ...piece } : null)));

  await executeGravityAnimation(gridSnapshot, targetPieces, initialYOffsets);
}

/**
 * 盤面を初期化
 * @param {Board} board - Boardインスタンス
 * @param {Object} options - オプション
 * @param {string} options.mode - 初期化モード ('random' | 'noMatches')
 *   - 'random': マッチあり可（デフォルト、スキルシャッフル用）
 *   - 'noMatches': マッチゼロ（Start直後用）
 */
async function initializePieces(board, options = {}) {
  const { mode = 'random' } = options;

  board.clear();
  initializeSpecialPieces();

  if (mode === 'noMatches') {
    // マッチゼロ生成 + 波状落下演出
    await seedBoardWithoutMatches(board);
    await animateBoardWaveEntry(board);
  } else {
    // 従来のランダム生成（マッチあり可）
    await applyGravityAndFill(board);
  }
}

async function clearBoardSmooth(board) {
  // シンプルにクリア
  board.clear();
  await new Promise((resolve) => setTimeout(resolve, 100));
}

async function ejectAllPiecesWithGravity(board) {
  // 直接実装
  board.clear();

  // アニメーション実行
  const { ejectAllPieces } = await import('../graphics/animation/result-animations.js');
  await ejectAllPieces();
}

export {
  addPiece,
  removePieces,
  initializePieces,
  clearBoardSmooth,
  ejectAllPiecesWithGravity,
  applyGravityAndFill,
  animateBoardWaveEntry,
};
